export type ModuleFilterValues = Record<string, unknown>;

const STORAGE_KEY = 'suite241.moduleFilters.v1';

type StoredModuleFilters = Record<
  string,
  {
    visible?: boolean;
    values?: ModuleFilterValues;
  }
>;

const hasStorage = (): boolean =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const readStorage = (): StoredModuleFilters => {
  if (!hasStorage()) return {};

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    console.warn('Filtres modules illisibles', error);
    return {};
  }
};

const writeStorage = (payload: StoredModuleFilters): void => {
  if (!hasStorage()) return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.warn('Filtres modules non enregistrés', error);
  }
};

export const loadModuleFilterState = <T extends ModuleFilterValues>(
  module: string,
  defaults: T,
  defaultVisible = false
): { visible: boolean; values: T } => {
  const store = readStorage();
  const current = store[module] || {};

  return {
    visible:
      typeof current.visible === 'boolean'
        ? current.visible
        : defaultVisible,
    values: {
      ...defaults,
      ...(current.values || {}),
    } as T,
  };
};

export const saveModuleFilterValues = (
  module: string,
  values: ModuleFilterValues
): void => {
  const store = readStorage();
  store[module] = {
    ...(store[module] || {}),
    values,
  };
  writeStorage(store);
};

export const saveModuleFilterVisibility = (
  module: string,
  visible: boolean
): void => {
  const store = readStorage();
  store[module] = {
    ...(store[module] || {}),
    visible,
  };
  writeStorage(store);
};

export const cleanFilterParams = (
  values: ModuleFilterValues
): ModuleFilterValues => {
  return Object.entries(values).reduce<ModuleFilterValues>((params, [key, value]) => {
    if (value === null || value === undefined || value === '') {
      return params;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return params;
      params[key] = trimmed;
      return params;
    }

    if (typeof value === 'boolean') {
      params[key] = value ? '1' : '0';
      return params;
    }

    params[key] = value;
    return params;
  }, {});
};

const normalizeFilterValue = (value: unknown): unknown => {
  if (value === null || value === undefined) return '';

  if (typeof value === 'string') {
    return value.trim();
  }

  if (Array.isArray(value)) {
    return value.map((entry) => normalizeFilterValue(entry));
  }

  return value;
};

const areFilterValuesEqual = (first: unknown, second: unknown): boolean => {
  return JSON.stringify(normalizeFilterValue(first)) === JSON.stringify(normalizeFilterValue(second));
};

export const activeFilterKeys = (
  values: ModuleFilterValues,
  defaults: ModuleFilterValues,
  ignoredKeys: string[] = []
): string[] => {
  const ignored = new Set(ignoredKeys);
  const keys = new Set([...Object.keys(defaults), ...Object.keys(values)]);

  return [...keys].filter((key) => (
    !ignored.has(key)
    && !areFilterValuesEqual(values[key], defaults[key])
  ));
};

export const countActiveFilters = (
  values: ModuleFilterValues,
  defaults: ModuleFilterValues,
  ignoredKeys: string[] = []
): number => activeFilterKeys(values, defaults, ignoredKeys).length;

export const activeFilterLabels = (
  values: ModuleFilterValues,
  defaults: ModuleFilterValues,
  labels: Record<string, string>,
  ignoredKeys: string[] = []
): string[] => activeFilterKeys(values, defaults, ignoredKeys)
  .map((key) => labels[key] || key);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export const extractApiList = <T = unknown>(payload: unknown): T[] => {
  if (Array.isArray(payload)) return payload as T[];
  if (!isRecord(payload)) return [];

  const data = payload.data;
  if (Array.isArray(data)) return data as T[];
  if (isRecord(data) && Array.isArray(data.data)) return data.data as T[];

  return [];
};

export const extractApiTotal = (payload: unknown, fallback = 0): number => {
  if (!isRecord(payload)) return fallback;

  const meta = payload.meta;
  if (isRecord(meta)) {
    const pagination = meta.pagination;
    if (isRecord(pagination) && Number.isFinite(Number(pagination.total))) {
      return Number(pagination.total);
    }

    if (Number.isFinite(Number(meta.total))) {
      return Number(meta.total);
    }
  }

  if (Number.isFinite(Number(payload.total))) {
    return Number(payload.total);
  }

  const data = payload.data;
  if (isRecord(data) && Number.isFinite(Number(data.total))) {
    return Number(data.total);
  }

  return fallback;
};
